/**
 * OWNER-OPERATING-SYSTEM-V1 -- the owner Home view model (pure; no fetch,
 * no React). Maps the /api/owner/home wire onto the band structure the
 * HOME RULING fixes: hero window band -> needs-attention band -> month
 * band -> quiet seam strip -> operations row, plus the SEPARATE print
 * sheet (never inline).
 *
 * Vocabulary REUSE (one home per vocabulary): leakage class labels + age
 * words come from the ONE sla-leakage view module; role tokens render via
 * the shared roleLabel; money renders via THE ONE INR formatter pair
 * (compact at hero grain, full precision on the print sheet -- the
 * FORMATTER LAW, s.20.5).
 */

import type {
  OwnerHomeComparisonRow,
  OwnerHomeIssueClassRow,
  OwnerHomeAnomalyRow,
  OwnerHomeDayCloseState,
  OwnerHomeWire,
} from '@/dashboard/types';
import { differenceReasonLabel } from '@/dashboard/money/money-desk-view';
import {
  classShortLabel,
  formatLeakageAge,
  isKnownBreachClass,
} from '@/dashboard/sla-leakage/sla-leakage-view';
import { roleLabel } from '@/dashboard/ui';
import {
  formatMinorUnitsInr,
  formatMinorUnitsInrCompact,
} from '@/dashboard/billing/billing-entry-view';

const DAY_LABEL = new Intl.DateTimeFormat('en-IN', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});

/** 'YYYY-MM-DD' (an IST business date) -> 'Fri 01 Aug'. Calendar-safe: the
 *  date string is parsed at UTC noon so no offset can shift the day. */
export function businessDateLabel(date: string): string {
  const t = Date.parse(`${date}T12:00:00Z`);
  if (Number.isNaN(t)) return date;
  return DAY_LABEL.format(new Date(t));
}

export function inclusiveEndDate(toDateExclusive: string): string {
  const t = Date.parse(`${toDateExclusive}T12:00:00Z`) - 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

/** Band title: single-day windows name the day; ranges name both ends. */
export function windowTitle(label: string, fromDate: string, toDateExclusive: string): string {
  const endInclusive = inclusiveEndDate(toDateExclusive);
  if (fromDate === endInclusive) {
    return `${label} -- ${businessDateLabel(fromDate)}`;
  }
  return `${label} -- ${businessDateLabel(fromDate)} to ${businessDateLabel(endInclusive)}`;
}

/** The band-header comparison basis, printed ONCE (25.10). */
export function comparisonBasis(comparisons: readonly OwnerHomeComparisonRow[]): string | null {
  const first = comparisons[0];
  if (!first) return null;
  const endInclusive = inclusiveEndDate(first.toDateExclusive);
  if (first.fromDate === endInclusive) {
    return `vs ${businessDateLabel(first.fromDate)}`;
  }
  return `vs ${businessDateLabel(first.fromDate)} to ${businessDateLabel(endInclusive)}`;
}

export interface DeltaView {
  text: string;
  direction: 'up' | 'down' | 'flat';
}

/** Signed printed delta (never color-alone); null pct -> no delta chip
 *  (an honest absence over an empty prior window, never a fake 0%). */
export function deltaView(pct: number | null): DeltaView | undefined {
  if (pct === null) return undefined;
  if (pct === 0) return { text: '0%', direction: 'flat' };
  return {
    text: `${pct > 0 ? '+' : ''}${pct}%`,
    direction: pct > 0 ? 'up' : 'down',
  };
}

export function heroMoneyValue(minorUnits: number): string {
  return formatMinorUnitsInrCompact(minorUnits);
}

export function preciseMoneyValue(minorUnits: number): string {
  return formatMinorUnitsInr(minorUnits).replace(/^INR/, 'Rs');
}

// --- R-CD-B3: the day-close band (stored money-desk facts, view-only) -----

const IST_TIME_LABEL = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});

/** One row per (clinic x business day). Money words reuse THE ONE INR
 *  formatter; the reason vocabulary reuses the money desk's own label home
 *  (differenceReasonLabel) -- never a second map. */
export interface DayCloseRowView {
  key: string;
  clinicName: string;
  dayLabel: string;
  status: OwnerHomeDayCloseState['status'];
  statusLabel: string;
  counted: string | null;
  expected: string | null;
  /** 'Exact' | 'Rs X short' | 'Rs X over'; null when not closed. */
  differenceText: string | null;
  differenceTone: 'exact' | 'flagged' | null;
  reasonLine: string | null;
  closedByLine: string | null;
}

const DAY_CLOSE_STATUS_LABELS: Record<OwnerHomeDayCloseState['status'], string> = {
  closed: 'Closed',
  first_count: 'Day started (first count)',
  not_closed: 'Not closed',
};

function dayCloseStateRow(
  clinicId: number,
  clinicName: string,
  slot: 'Today' | 'Yesterday',
  state: OwnerHomeDayCloseState,
): DayCloseRowView {
  const closed = state.closed;
  let differenceText: string | null = null;
  let differenceTone: 'exact' | 'flagged' | null = null;
  let reasonLine: string | null = null;
  let closedByLine: string | null = null;
  if (closed !== null) {
    if (closed.differenceMinorUnits === 0) {
      differenceText = 'Exact';
      differenceTone = 'exact';
    } else {
      const magnitude = preciseMoneyValue(Math.abs(closed.differenceMinorUnits));
      differenceText = `${magnitude} ${closed.differenceMinorUnits < 0 ? 'short' : 'over'}`;
      differenceTone = 'flagged';
    }
    if (closed.differenceReasonCategory !== null) {
      reasonLine =
        differenceReasonLabel(closed.differenceReasonCategory) +
        (closed.differenceReasonText !== null && closed.differenceReasonText !== ''
          ? ` - ${closed.differenceReasonText}`
          : '');
    }
    closedByLine =
      `${closed.acknowledgedByLabel} at ${IST_TIME_LABEL.format(new Date(closed.acknowledgedAt))} IST` +
      (closed.verifiedByDoctorName !== null
        ? `, verified by ${closed.verifiedByDoctorName}`
        : '');
  }
  return {
    key: `${clinicId}-${state.businessDate}`,
    clinicName,
    dayLabel: `${slot} (${businessDateLabel(state.businessDate)})`,
    status: state.status,
    statusLabel: DAY_CLOSE_STATUS_LABELS[state.status],
    counted: closed !== null ? preciseMoneyValue(closed.countedCloseMinorUnits) : null,
    expected: closed !== null ? preciseMoneyValue(closed.derivedCloseMinorUnits) : null,
    differenceText,
    differenceTone,
    reasonLine,
    closedByLine,
  };
}

export function dayCloseRows(wire: OwnerHomeWire): DayCloseRowView[] {
  const rows: DayCloseRowView[] = [];
  for (const clinic of wire.dayClose.clinics) {
    rows.push(
      dayCloseStateRow(clinic.clinicId, clinic.clinicName, 'Today', clinic.today),
      dayCloseStateRow(clinic.clinicId, clinic.clinicName, 'Yesterday', clinic.yesterday),
    );
  }
  return rows;
}

export interface IssueRowView {
  key: string;
  severity: 'red' | 'amber' | 'neutral';
  glyph: '!' | 'o';
  headline: string;
  ownerLine: string;
  ageText: string | null;
  ageTone: 'red' | 'amber' | 'neutral';
}

/** Leakage class row -> the ALERT-row facts (glyph + fact + owner role AND
 *  person + printed age; 25.2). Class tokens translate through the ONE
 *  sla-leakage vocabulary; unknown tokens render their raw token NEVER a
 *  guessed label (honest, and grep-visible). */
export function issueRowFromClass(row: OwnerHomeIssueClassRow): IssueRowView {
  const label = isKnownBreachClass(row.breachClass)
    ? classShortLabel(row.breachClass)
    : row.breachClass;
  const person = row.assigneeSummary ?? 'Unassigned';
  const role = roleLabel(row.ownerRoleToken);
  return {
    key: `class:${row.breachClass}`,
    severity: row.severity,
    glyph: row.severity === 'red' ? '!' : 'o',
    headline: `${row.count} ${label.toLowerCase()}`,
    ownerLine: `${person} -- ${role}`,
    ageText: row.worstAgeMinutes === null ? null : formatLeakageAge(row.worstAgeMinutes),
    ageTone: row.severity === 'red' ? 'red' : row.severity === 'amber' ? 'amber' : 'neutral',
  };
}

export function issueRowFromAnomaly(row: OwnerHomeAnomalyRow, index: number): IssueRowView {
  const severity = row.severity === 'critical' ? 'red' : 'amber';
  return {
    key: `anomaly:${index}`,
    severity,
    glyph: severity === 'red' ? '!' : 'o',
    headline: row.description !== '' ? row.description : row.flagType,
    ownerLine: `${row.clinicName} -- clinic flag`,
    ageText: null,
    ageTone: severity,
  };
}

export function allIssueRows(wire: OwnerHomeWire): IssueRowView[] {
  const classRows = wire.issues.classes.map(issueRowFromClass);
  const anomalyRows = wire.issues.anomalies.map(issueRowFromAnomaly);
  const rank = (r: IssueRowView): number => (r.severity === 'red' ? 0 : r.severity === 'amber' ? 1 : 2);
  return [...classRows, ...anomalyRows].sort((a, b) => rank(a) - rank(b));
}

export interface PrintRowView {
  metric: string;
  current: string;
  priors: string[];
  delta: string;
}

/** The print sheet's full-precision rows (O-1 simplest-tabular; 20.5: the
 *  print view is the precision surface). Absent facts print '--', never 0. */
export function printRows(wire: OwnerHomeWire): PrintRowView[] {
  const w = wire.window;
  const m = wire.monthToDate;
  const dash = '--';
  const money = (v: number | null): string => (v === null ? dash : preciseMoneyValue(v));
  const count = (v: number | null): string => (v === null ? dash : String(v));
  const pct = (v: number | null): string =>
    v === null ? dash : `${v > 0 ? '+' : ''}${v}%`;
  return [
    {
      metric: `Business (collected, Rs) -- ${w.label}`,
      current: money(w.business === null ? null : w.business.totalMinorUnits),
      priors: w.comparisons.map((c) => money(c.businessMinorUnits)),
      delta: pct(w.comparisons[0]?.businessDeltaPct ?? null),
    },
    {
      metric: `Walk-ins (arrivals) -- ${w.label}`,
      current: count(w.walkins),
      priors: w.comparisons.map((c) => count(c.walkins)),
      delta: pct(w.comparisons[0]?.walkinsDeltaPct ?? null),
    },
    {
      metric: `Appointments booked -- ${w.label}`,
      current: count(w.booked),
      priors: w.comparisons.map((c) => count(c.booked)),
      delta: pct(w.comparisons[0]?.bookedDeltaPct ?? null),
    },
    {
      metric: 'Business, month to date (Rs)',
      current: money(m.businessMinorUnits),
      priors: m.comparisons.map((c) => money(c.businessMinorUnits)),
      delta: pct(m.comparisons[0]?.businessDeltaPct ?? null),
    },
    {
      metric: 'Walk-ins, month to date',
      current: count(m.walkins),
      priors: m.comparisons.map((c) => count(c.walkins)),
      delta: pct(m.comparisons[0]?.walkinsDeltaPct ?? null),
    },
  ];
}

/** Month spark values (real per-day sums; empty when the read failed). */
export function monthSpark(wire: OwnerHomeWire): { values: number[]; caption: string } | undefined {
  const series = wire.monthToDate.businessDailySeries;
  if (series === null || series.length === 0) return undefined;
  const from = businessDateLabel(wire.monthToDate.fromDate);
  const to = businessDateLabel(inclusiveEndDate(wire.monthToDate.toDateExclusive));
  return {
    values: series.map((p) => p.minorUnits),
    caption: `daily, ${from} to ${to}`,
  };
}
